/**
 * useAudio React Hook
 * 
 * Provides easy access to the AudioManager from React components.
 * Handles initialization on first user interaction and provides
 * all audio control functions.
 * 
 * Usage:
 *   const { playUI, playEvent, isReady, settings } = useAudio();
 *   
 *   onClick={() => playUI('click')}
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { audioManager } from '../AudioManager.js';
import { UI_SOUNDS, EVENT_SOUNDS, AMBIENT_SOUNDS } from '../audioConfig.js';

/**
 * Main audio hook for React components
 */
export function useAudio() {
  const [isReady, setIsReady] = useState(audioManager.initialized);
  const [isMuted, setIsMuted] = useState(audioManager.isMuted());
  const [settings, setSettings] = useState(audioManager.getSettings());
  const initAttempted = useRef(false);

  // ===================================================================
  // STATE SYNC - Keep React state in sync with AudioManager
  // ===================================================================

  useEffect(() => {
    // Sync state periodically to catch changes from other sources
    // (keyboard shortcuts, other hook instances, etc.)
    const syncState = () => {
      const managerMuted = audioManager.isMuted();
      const managerReady = audioManager.initialized;
      
      if (managerMuted !== isMuted) {
        setIsMuted(managerMuted);
      }
      if (managerReady !== isReady) {
        setIsReady(managerReady);
        if (managerReady) {
          setSettings(audioManager.getSettings());
        }
      }
    };
    
    // Check immediately and then periodically
    syncState();
    const interval = setInterval(syncState, 250);
    
    return () => clearInterval(interval);
  }, [isMuted, isReady]);

  // ===================================================================
  // INITIALIZATION
  // ===================================================================

  /**
   * Initialize audio on first user interaction
   * Should be called from a click/keypress handler
   */
  const initializeAudio = useCallback(async () => {
    if (initAttempted.current && audioManager.initialized) {
      return true;
    }
    
    initAttempted.current = true;
    const success = await audioManager.initialize();
    
    if (success) {
      setIsReady(true);
      setSettings(audioManager.getSettings());
    }
    
    return success;
  }, []);

  // Try to initialize on first interaction with the document
  useEffect(() => {
    const handleInteraction = async () => {
      if (!audioManager.initialized) {
        await initializeAudio();
      }
    };

    // Listen for first user interaction
    document.addEventListener('click', handleInteraction, { once: true });
    document.addEventListener('keydown', handleInteraction, { once: true });

    return () => {
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('keydown', handleInteraction);
    };
  }, [initializeAudio]);

  // ===================================================================
  // PLAYBACK FUNCTIONS
  // ===================================================================

  /**
   * Play a UI sound
   * @param {string} name - Sound name from UI_SOUNDS
   */
  const playUI = useCallback((name) => {
    // Check manager directly - state may be stale across hook instances
    if (!audioManager.initialized) return;
    audioManager.playUI(name);
  }, []);

  /**
   * Play a game event sound
   * @param {string} name - Sound name from EVENT_SOUNDS
   */
  const playEvent = useCallback((name) => {
    // Check manager directly - state may be stale across hook instances
    if (!audioManager.initialized) return;
    audioManager.playEvent(name);
  }, []);

  /**
   * Play ambient sound
   * @param {string} name - Ambient name from AMBIENT_SOUNDS
   */
  const playAmbient = useCallback((name) => {
    // Check manager directly - state may be stale across hook instances
    if (!audioManager.initialized) return;
    audioManager.playAmbient(name);
  }, []);

  /**
   * Stop ambient sound
   */
  const stopAmbient = useCallback(() => {
    audioManager.stopAmbient();
  }, []);

  /**
   * Play music
   * @param {string} name - Music track name ('strategic', 'combat', etc.)
   */
  const playMusic = useCallback((name) => {
    // Check manager directly - state may be stale across hook instances
    if (!audioManager.initialized) return;
    audioManager.playMusic(name);
  }, []);

  /**
   * Stop music
   */
  const stopMusic = useCallback(() => {
    audioManager.stopMusic();
  }, []);

  // ===================================================================
  // VOLUME CONTROL
  // ===================================================================

  /**
   * Set volume for a category
   */
  const setVolume = useCallback((category, value) => {
    audioManager.setVolume(category, value);
    setSettings(audioManager.getSettings());
  }, []);

  /**
   * Toggle mute
   */
  const toggleMute = useCallback(() => {
    const newMuted = audioManager.toggleMute();
    setIsMuted(newMuted);
    setSettings(audioManager.getSettings());
    return newMuted;
  }, []);

  /**
   * Mute all
   */
  const muteAll = useCallback(() => {
    audioManager.muteAll();
    setIsMuted(true);
    setSettings(audioManager.getSettings());
  }, []);

  /**
   * Unmute all
   */
  const unmuteAll = useCallback(() => {
    audioManager.unmuteAll();
    setIsMuted(false);
    setSettings(audioManager.getSettings());
  }, []);

  // ===================================================================
  // RETURN HOOK API
  // ===================================================================

  return {
    // State
    isReady,
    isMuted,
    settings,
    isAvailable: audioManager.isAvailable(),

    // Initialization
    initializeAudio,

    // Playback
    playUI,
    playEvent,
    playAmbient,
    stopAmbient,
    playMusic,
    stopMusic,

    // Volume control
    setVolume,
    toggleMute,
    muteAll,
    unmuteAll,

    // Sound name constants for convenience
    sounds: {
      ui: UI_SOUNDS,
      events: EVENT_SOUNDS,
      ambient: AMBIENT_SOUNDS,
    },
  };
}

// =====================================================================
// SPECIALIZED HOOKS
// =====================================================================

/**
 * Hook specifically for UI sounds
 * Lighter weight if you only need UI sounds
 */
export function useUISound() {
  const { playUI, isReady } = useAudio();
  
  return {
    click: useCallback(() => playUI('click'), [playUI]),
    hover: useCallback(() => playUI('hover'), [playUI]),
    confirm: useCallback(() => playUI('confirm'), [playUI]),
    cancel: useCallback(() => playUI('cancel'), [playUI]),
    alert: useCallback(() => playUI('alert'), [playUI]),
    isReady,
  };
}

/**
 * Hook for game event sounds
 */
export function useEventSound() {
  const { playEvent, isReady } = useAudio();
  
  return {
    endTurn: useCallback(() => playEvent('endTurn'), [playEvent]),
    combatInitiated: useCallback(() => playEvent('combatInitiated'), [playEvent]),
    combatVictory: useCallback(() => playEvent('combatVictory'), [playEvent]),
    combatDefeat: useCallback(() => playEvent('combatDefeat'), [playEvent]),
    territoryCaptured: useCallback(() => playEvent('territoryCaptured'), [playEvent]),
    playerEliminated: useCallback(() => playEvent('playerEliminated'), [playEvent]),
    resourceGained: useCallback(() => playEvent('resourceGained'), [playEvent]),
    techUnlocked: useCallback(() => playEvent('techUnlocked'), [playEvent]),
    isReady,
  };
}

/**
 * Hook for ambient/atmosphere
 */
export function useAmbientSound() {
  const { playAmbient, stopAmbient, initializeAudio } = useAudio();
  const [isReady, setIsReady] = useState(audioManager.initialized);
  
  // Sync ready state with manager
  useEffect(() => {
    const checkReady = () => {
      if (audioManager.initialized && !isReady) {
        setIsReady(true);
      }
    };
    
    // Check periodically since initialization happens async
    const interval = setInterval(checkReady, 100);
    checkReady();
    
    return () => clearInterval(interval);
  }, [isReady]);
  
  // Try to auto-initialize on user interaction
  useEffect(() => {
    const handleInteraction = async () => {
      if (!audioManager.initialized) {
        await initializeAudio();
        setIsReady(audioManager.initialized);
      }
    };
    
    document.addEventListener('click', handleInteraction, { once: true });
    return () => document.removeEventListener('click', handleInteraction);
  }, [initializeAudio]);
  
  return {
    startWarRoom: useCallback(() => playAmbient('warRoom'), [playAmbient]),
    startTension: useCallback(() => playAmbient('tension'), [playAmbient]),
    stop: stopAmbient,
    isReady,
  };
}

export default useAudio;
